import { UNTRANSLATED_LOCALE } from '@va/constants';
import { T3_URL_PARAMS } from '@va/standalone/shared/constants';
import { DatePickerShortcut, DateRangeKey } from '@va/types/time';
import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import moment, { Moment } from 'moment-timezone';
import { getBrowserLanguage } from '../browser';
import { getParameterByName } from '../url';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Apply website timezone to a UTC date
 */
export const inWebsiteTimezone = (date?: dayjs.ConfigType) =>
  window.timezone ? dayjs(date).tz(window.timezone) : dayjs(date);

export function getFilterDateWithTimezone(date: Moment, timezone: string) {
  const changedDate = date.clone();

  //add timezone for date
  changedDate.tz(timezone);

  changedDate.add(date.utcOffset() - changedDate.utcOffset(), 'minutes');

  return changedDate;
}

const DEFAULT_DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
};

const LOCALIZED_DATE_TIME_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: 'numeric',
  minute: 'numeric',
};

export const FREE_PLAN_HISTORY_DAYS = 40;

export const DATEPICKER_DEFAULT_SHORTCUTS: DatePickerShortcut[] = [
  { key: 'today', transKey: 'datePicker.period.today' },
  { key: 'yesterday', transKey: 'datePicker.period.yesterday' },
  { key: 'last7Days', transKey: 'datePicker.period.lastDays', diff: 7 },
  { key: 'last30Days', transKey: 'datePicker.period.lastDays', diff: 30, isDefault: true },
  { key: 'thisMonth', transKey: 'datePicker.period.thisMonth' },
  { key: 'lastMonth', transKey: 'datePicker.period.lastMonth' },
  { key: 'prev6Month', transKey: 'datePicker.period.prevMonths', diff: 6 },
  { key: 'prev12Month', transKey: 'datePicker.period.prevMonths', diff: 12 },
  { key: 'thisYear', transKey: 'datePicker.period.thisYear' },
  { key: 'lastYear', transKey: 'datePicker.period.lastYear' },
  { key: 'allTime', transKey: 'datePicker.period.allTime' },
];

export const DATEPICKER_FREE_PLAN_HISTORY_SHORTCUT: DatePickerShortcut = {
  key: 'last40Days',
  transKey: 'datePicker.period.lastDays',
  diff: FREE_PLAN_HISTORY_DAYS,
};

export const ALL_DATEPICKER_SHORTCUTS = [...DATEPICKER_DEFAULT_SHORTCUTS, DATEPICKER_FREE_PLAN_HISTORY_SHORTCUT];

const getIntlLocale = (locale: string) => {
  if (locale !== 'en' && locale !== UNTRANSLATED_LOCALE) return locale;

  const browserLocale = getBrowserLanguage();

  return browserLocale.indexOf('en') === 0 ? browserLocale : 'en-US';
};

export function getLocalizedDate(timestamp: number, locale: string, options = DEFAULT_DATE_FORMAT_OPTIONS) {
  const date = new Date(timestamp * 1000);
  const formatOptions = { ...DEFAULT_DATE_FORMAT_OPTIONS, ...options };

  return date.toLocaleDateString(getIntlLocale(locale), formatOptions);
}

export const getTimezoneName = (date: Dayjs, locale: string, timezone?: string) => {
  const intlLocale = getIntlLocale(locale);

  return new Intl.DateTimeFormat(intlLocale.indexOf('en') === 0 ? 'en-GB' : intlLocale, {
    timeZone: timezone,
    timeZoneName: 'short',
  })
    .formatToParts(date.toDate())
    .find((part) => part.type === 'timeZoneName')?.value;
};

export const getFormattedDateAndTime = (unixTime: number, locale: string) => {
  return unixTime ? getLocalizedDate(unixTime, locale, LOCALIZED_DATE_TIME_FORMAT_OPTIONS) : '-';
};

export const getTimestampFromIso = (iso: string | moment.Moment) => {
  return moment(iso).unix();
};

export const ensureStartBeforeEnd = ([start, end]: [Moment, Moment]) => {
  return moment(end).isBefore(start) ? [end, start] : [start, end];
};

export const getDaysDifference = (start: string, end: string): number => {
  return dayjs(end).diff(dayjs(start), 'day');
};

export const getYearsDifference = (start: number, end: number, float?: boolean): number => {
  return dayjs(end).diff(dayjs(start), 'year', float);
};

/** Similar to `Dayjs.isBetween()`, but correctly works when min/max date is not defined */
export const isDateInRange = (
  date: Dayjs,
  minDate: dayjs.ConfigType,
  maxDate: dayjs.ConfigType,
  interval?: dayjs.OpUnitType,
) => {
  if (minDate && date.isBefore(minDate, interval)) {
    return false;
  }

  if (maxDate && date.isAfter(maxDate, interval)) {
    return false;
  }

  return true;
};

export const isDate = (input: string) => {
  return dayjs(input).isValid();
};

export const getTimezone = (date: Moment | Dayjs) => {
  if (moment.isMoment(date)) {
    return date.tz();
  }

  // @ts-ignore
  return date['$x']?.['$timezone'];
};

export const fromDayjsToMoment = (date: Dayjs): Moment => {
  const timezone = getTimezone(date);

  if (timezone) return moment(date.toISOString()).tz(timezone);

  return moment(date.toISOString());
};

export const fromMomentToDayjs = (date: Moment): Dayjs => {
  const timezone = date.tz();

  if (timezone) return dayjs(date.toISOString()).tz(timezone);

  return dayjs(date.toISOString());
};

export const getDateRange = (
  key: DateRangeKey,
  diff: number | undefined = 0,
  minDate: dayjs.ConfigType,
  timezone?: string,
) => {
  const data: Record<DateRangeKey, { start: Dayjs; end: Dayjs }> = {
    today: { start: dayjs(), end: dayjs() },
    yesterday: { start: dayjs().subtract(1, 'day'), end: dayjs().subtract(1, 'day') },
    last7Days: { start: dayjs().subtract(diff - 1, 'day'), end: dayjs() },
    last30Days: { start: dayjs().subtract(diff - 1, 'day'), end: dayjs() },
    thisMonth: { start: dayjs().date(1), end: dayjs() },
    last40Days: { start: dayjs().subtract(diff - 1, 'day'), end: dayjs() },
    lastMonth: {
      start: dayjs().date(1).subtract(1, 'month'),
      end: dayjs().date(0),
    },
    prev6Month: {
      start: dayjs().subtract(diff, 'month').add(1, 'day'),
      end: dayjs(),
    },
    prev12Month: {
      start: dayjs().subtract(diff, 'month').add(1, 'day'),
      end: dayjs(),
    },
    thisYear: {
      start: dayjs().year(dayjs().year()).month(0).date(1),
      end: dayjs(),
    },
    lastYear: {
      start: dayjs()
        .year(dayjs().year() - 1)
        .month(0)
        .date(1),
      end: dayjs()
        .year(dayjs().year() - 1)
        .month(11)
        .date(31),
    },
    allTime: {
      start: dayjs(minDate),
      end: dayjs(),
    },
  };

  const { start, end } = data[key];

  if (timezone) return { start: start.tz(timezone, true).startOf('day'), end: end.tz(timezone, true).endOf('day') };

  return { start: start.startOf('day'), end: end.endOf('day') };
};

export const isDateRangeKey = (value: unknown): value is DateRangeKey =>
  ALL_DATEPICKER_SHORTCUTS.some((shortcut) => shortcut.key === value);

export const getShortcutDateRange = (key: DateRangeKey, minDate: dayjs.ConfigType, timezone?: string) => {
  const shortcut = ALL_DATEPICKER_SHORTCUTS.find((item) => item.key === key);
  return getDateRange(key, shortcut?.diff, minDate, timezone);
};

/**
 * A preset (`startEndDate`) is recalculated relative to the current date, so bookmarked/shared links always show the latest period.
 * A custom range (`startDate` + `endDate`) is used as is.
 */
export const getGlobalFilterFromUrl = (
  timezone: string,
  installDate: number | undefined,
): { from: Moment; until: Moment; dateRangeKey?: DateRangeKey } | null => {
  const dateRangeKey = getParameterByName(T3_URL_PARAMS.startEndDate);

  if (isDateRangeKey(dateRangeKey)) {
    // `allTime` can't be resolved before the install date is known
    if (dateRangeKey === 'allTime' && !installDate) return null;

    const minDate = installDate ? dayjs(installDate * 1000).tz(timezone) : undefined;
    const { start, end } = getShortcutDateRange(dateRangeKey, minDate, timezone);

    return { from: fromDayjsToMoment(start), until: fromDayjsToMoment(end), dateRangeKey };
  }

  const startDate = getParameterByName(T3_URL_PARAMS.startDate);
  const endDate = getParameterByName(T3_URL_PARAMS.endDate);
  if (!startDate || !endDate) return null;

  return {
    from: moment(startDate, 'YYYY-MM-DD').tz(timezone, true).startOf('day'),
    until: moment(endDate, 'YYYY-MM-DD').tz(timezone, true).endOf('day'),
  };
};

export const findMatchingShortcut = (
  shortcuts: DatePickerShortcut[],
  [dateStart, dateEnd]: [Dayjs, Dayjs],
  minDate?: Dayjs,
): DatePickerShortcut | undefined => {
  return shortcuts.find((shortcut) => {
    const { key, diff } = shortcut;
    const timezone = dateStart ? getTimezone(dateStart) : undefined;
    const { start: shortcutStart, end: shortcutEnd } = getDateRange(key, diff, minDate, timezone);

    return dateStart.valueOf() === shortcutStart.valueOf() && dateEnd.valueOf() === shortcutEnd.valueOf();
  });
};
